import React, { ImgHTMLAttributes, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { SmallLoader } from 'components/Loader';
import IntersectionObserver from 'components/common/IntersectionObserver';
import { combineSideEffects } from 'helpers/index';
import { BLANK_IMAGE_PLACEHOLDER } from 'constants/appConstants';

import css from 'styles/components/common/image.scss';

const defaultProps = {
  placeholder: <div className={css.placeholder}><SmallLoader/></div>,
  forceLoad: false
};

// remove placeholder global html attribute from the type so its not mistaken as the placeholder for the LazyImage
export type ImageWithoutPlaceholderProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'placeholder'>;
export type ImageProps = ImageWithoutPlaceholderProps & Partial<typeof defaultProps>;
const Image = ({
  onLoad,
  className,
  placeholder = defaultProps.placeholder,
  forceLoad = defaultProps.forceLoad,
  ...rest
}: ImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const showPlaceholder = placeholder && !loaded && !forceLoad;
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Handles removing loading class from cached images
    ref && ref.current && ref.current?.complete && setLoaded(true);
  }, []);

  return <>
    {showPlaceholder && placeholder}
    <img
      ref={ref}
      alt=""
      className={cn(className, css.image, { [css.loaded] : loaded })}
      onLoad={combineSideEffects(onLoad, () => setLoaded(true))}
      {...rest}/>
  </>;
};

export const LazyImage = ({ placeholder, forceLoad, ...rest }: ImageProps) => {
  const placeholderWithEmptyImage = <>
    {placeholder}
    {rest.alt && <img src={BLANK_IMAGE_PLACEHOLDER} className="screenReadersOnly" alt={rest.alt} />} {/* hidden blank image place holder for screen readers. */}
  </>;
  return <IntersectionObserver placeholder={placeholderWithEmptyImage} forceLoad={forceLoad}>
    <Image placeholder={placeholder} forceLoad={forceLoad} {...rest}/>
  </IntersectionObserver>;
};

export default Image;
